/**
 * Auto-shrink for the sum block (docs/GAME_DESIGN.md § 8: math text ≥ 20 px,
 * shrinking to fit the block's width down to 16 px).
 *
 * The expression never wraps, so its size scales linearly with font size: one
 * measurement at any size tells us the size that fits. Measuring again at the
 * fitted size gives the same answer, so the component can re-measure on every
 * resize or new problem without oscillating.
 */

/** The block never shrinks math below this (px). */
export const MIN_MATH_PX = 16;

export interface FontFitInput {
  /** Content width measured at `measuredAtPx` (px, no padding). */
  naturalWidth: number;
  /** Content height measured at `measuredAtPx` (px). Omit to fit the width only. */
  naturalHeight?: number;
  /** Font size the content was measured at (px). */
  measuredAtPx: number;
  /** Room available (px). */
  availableWidth: number;
  availableHeight?: number;
  /** Preferred size: the result never exceeds it (px). */
  basePx: number;
  /** Smallest allowed size (px). Default MIN_MATH_PX. */
  minPx?: number;
}

export interface FontFit {
  /** Font size to use (px), rounded down to 0.5 px so sub-pixel re-measures don't jitter. */
  px: number;
  /** Even `minPx` is too big: the caller should let the content wrap or clip. */
  overflows: boolean;
}

const positive = (x: number | undefined): x is number => x !== undefined && Number.isFinite(x) && x > 0;

/** Largest font size in [minPx, basePx] at which the content fits the available room. */
export function fitFontSize(input: FontFitInput): FontFit {
  if (!positive(input.basePx)) return { px: input.minPx ?? MIN_MATH_PX, overflows: false };
  const minPx = Math.min(input.minPx ?? MIN_MATH_PX, input.basePx);
  if (!positive(input.measuredAtPx)) return { px: input.basePx, overflows: false };

  // Size at which one dimension exactly fills its room: available × measuredAt / natural.
  const limit = (natural: number | undefined, available: number | undefined): number =>
    positive(natural) && available !== undefined && Number.isFinite(available)
      ? (Math.max(0, available) * input.measuredAtPx) / natural
      : Infinity;
  const ideal = Math.min(input.basePx, limit(input.naturalWidth, input.availableWidth), limit(input.naturalHeight, input.availableHeight));

  return { px: Math.max(minPx, Math.floor(ideal * 2) / 2), overflows: ideal < minPx };
}
